import { useState } from 'react';

import { useLocale, useTranslations } from 'next-intl';

import { PATH_ICON } from '@/app/_constant/icons';
import { cn } from '@/app/_helper/tailwind';
import { FacilityIcon } from '@/listing/properti/_components/ListingInfo/ListingFacilities';
import { PropertyTypeEnum } from '@/modules/property/property.type';

type FacilityInfoProps = {
    bedroom?: string;
    bathroom?: string;
    carports?: string;
    buildingSize?: string;
    landSize?: string;
    className?: string;
    priceType?: string;
    propertyType?: string;
    roomSize?: string;
    kostFacilitiesRoom?: string;
    hotelFacilitiesRoom?: string;
    hotelFacilitiesNearby?: string;
    villaFacilitiesGeneral?: string;
    villaFacilitiesRoom?: string;
};

const FacilityInfo = ({
    bedroom,
    bathroom,
    carports,
    buildingSize,
    landSize,
    className,
    priceType,
    propertyType,
    roomSize,
    kostFacilitiesRoom,
    hotelFacilitiesRoom,
    villaFacilitiesGeneral,
}: FacilityInfoProps) => {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const t = useTranslations('ldp.recommendation.facilities');
    const locale = useLocale();

    return priceType === '0' ? (
        <div
            className={cn(
                'flex items-center gap-x-2 text-accent font-medium text-sm',
                className,
            )}
        >
            {Boolean(bedroom) && (
                <span className='flex items-center gap-x-1'>
                    <svg
                        className='text-greyText'
                        width='16'
                        height='14'
                        viewBox='0 0 16 14'
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'
                    >
                        <use
                            xlinkHref={`${PATH_ICON}SearchPageIcons.svg#bedroom-icon`}
                        />
                    </svg>
                    {bedroom}
                </span>
            )}
            {Boolean(bathroom) && (
                <span className='flex items-center gap-x-1'>
                    <svg
                        className='text-greyText'
                        width='14'
                        height='15'
                        viewBox='0 0 14 15'
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'
                    >
                        <use
                            xlinkHref={`${PATH_ICON}SearchPageIcons.svg#bathroom-icon`}
                        />
                    </svg>

                    {bathroom}
                </span>
            )}
            {Boolean(carports) && (
                <span className='flex items-center gap-x-1'>
                    <svg
                        className='text-greyText'
                        width='15'
                        height='15'
                        viewBox='0 0 15 15'
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'
                    >
                        <use
                            xlinkHref={`${PATH_ICON}SearchPageIcons.svg#carports-icon`}
                        />
                    </svg>

                    {carports}
                </span>
            )}
            {Boolean(buildingSize) && (
                <span>
                    <span className='text-greyText'>{`${t('bz')}: `}</span>
                    {buildingSize}
                </span>
            )}
            {Boolean(landSize) && (
                <span>
                    <span className='text-greyText'>{`${t('lz')}: `} </span>
                    {landSize}
                </span>
            )}
        </div>
    ) : (
        <div className='w-full rounded-lg'>
            <div className='py-2 overflow-hidden relative flex flex-row line-clamp-1 whitespace-nowrap gap-2'>
                {Boolean(kostFacilitiesRoom) && (
                    <div className='absolute right-0 w-[80px] flex justify-end bg-[linear-gradient(90deg,_rgba(255,_255,_255,_0)_0%,_#FFFFFF_50%)] -translate-y-1'>
                        <button
                            className='w-6 h-6 rounded-lg bg-white border-primary border-[1px] border-solid flex justify-center items-center z-50'
                            onClick={(event) => {
                                event.stopPropagation();
                                setIsOpen((prev) => !prev);
                            }}
                        >
                            <svg
                                width='13'
                                height='7'
                                viewBox='0 0 13 7'
                                fill='none'
                                xmlns='http://www.w3.org/2000/svg'
                            >
                                <path
                                    d='M0.455922 1.05289L5.87792 6.25789C6.07142 6.44389 6.37742 6.44389 6.57092 6.25789L11.9929 1.05289C12.1924 0.861387 12.1989 0.544387 12.0074 0.345387C11.8159 0.146387 11.4989 0.139387 11.2999 0.330887L5.87792 5.53589H6.57092L1.14892 0.330887C0.949422 0.139387 0.632922 0.145887 0.441422 0.345387C0.249922 0.544887 0.256422 0.861387 0.455922 1.05289Z'
                                    fill='#2951A3'
                                />
                            </svg>
                        </button>
                    </div>
                )}
                {Boolean(bedroom) &&
                    propertyType === PropertyTypeEnum.villa.toString() && (
                        <span className='flex items-center gap-x-1'>
                            <svg
                                className='text-greyText'
                                width='16'
                                height='14'
                                viewBox='0 0 16 14'
                                fill='none'
                                xmlns='http://www.w3.org/2000/svg'
                            >
                                <use
                                    xlinkHref={`${PATH_ICON}SearchPageIcons.svg#bedroom-icon`}
                                />
                            </svg>
                            <span className='text-sm ml-1 whitespace-nowrap'>
                                {bedroom}
                            </span>
                        </span>
                    )}
                {Boolean(bathroom) &&
                    propertyType === PropertyTypeEnum.villa.toString() && (
                        <span className='flex items-center gap-x-1'>
                            <svg
                                className='text-greyText'
                                width='14'
                                height='15'
                                viewBox='0 0 14 15'
                                fill='none'
                                xmlns='http://www.w3.org/2000/svg'
                            >
                                <use
                                    xlinkHref={`${PATH_ICON}SearchPageIcons.svg#bathroom-icon`}
                                />
                            </svg>

                            <span className='text-sm ml-1 whitespace-nowrap'>
                                {bathroom}
                            </span>
                        </span>
                    )}
                {Boolean(carports) &&
                    propertyType === PropertyTypeEnum.villa.toString() && (
                        <span className='flex items-center gap-x-1'>
                            <svg
                                className='text-greyText'
                                width='15'
                                height='15'
                                viewBox='0 0 15 15'
                                fill='none'
                                xmlns='http://www.w3.org/2000/svg'
                            >
                                <use
                                    xlinkHref={`${PATH_ICON}SearchPageIcons.svg#carports-icon`}
                                />
                            </svg>

                            <span className='text-sm ml-1 whitespace-nowrap'>
                                {carports}
                            </span>
                        </span>
                    )}
                {Boolean(roomSize) &&
                    propertyType !== PropertyTypeEnum.villa.toString() && (
                        <span className='flex items-center gap-x-1 text-sm'>
                            <svg
                                className='text-greyText translate-y-[-2px]'
                                width='16'
                                height='16'
                                viewBox='0 0 16 16'
                                fill='none'
                                xmlns='http://www.w3.org/2000/svg'
                            >
                                <use
                                    xlinkHref={`${PATH_ICON}SearchPageIcons.svg#room-size-icon`}
                                />
                            </svg>
                            <span className='text-sm ml-1'>{roomSize}</span>
                        </span>
                    )}
                {Boolean(hotelFacilitiesRoom) && (
                    <div className='flex items-center gap-1'>
                        <div className='flex items-center ml-2'>
                            {hotelFacilitiesRoom
                                ?.split(', ')
                                .slice(0, 2)
                                .map((hotel) => (
                                    <div
                                        key={`hotel-${hotel}`}
                                        className='w-5 h-5 rounded-full border-[1.25px] border-solid border-white bg-[#C1D6FF] p-0.5 flex items-center justify-center first:ml-0 -ml-1'
                                    >
                                        <FacilityIcon
                                            facility={hotel}
                                            size='14px'
                                        />
                                    </div>
                                ))}
                        </div>
                        <span className='text-sm'>
                            {hotelFacilitiesRoom?.split(', ').length}{' '}
                            {locale === 'en' ? 'Facilities' : 'Fasilitas'}
                        </span>
                    </div>
                )}
                {Boolean(villaFacilitiesGeneral) && (
                    <div className='flex items-center gap-1'>
                        <div className='flex items-center ml-2'>
                            {villaFacilitiesGeneral
                                ?.split(', ')
                                .slice(0, 2)
                                .map((villa) => (
                                    <div
                                        key={`villa-${villa}`}
                                        className='w-5 h-5 rounded-full border-[1.25px] border-solid border-white bg-[#C1D6FF] p-0.5 flex items-center justify-center first:ml-0 -ml-1'
                                    >
                                        <FacilityIcon
                                            facility={villa}
                                            size='14px'
                                        />
                                    </div>
                                ))}
                        </div>
                        <span className='text-sm'>
                            {villaFacilitiesGeneral?.split(', ').length}{' '}
                            {locale === 'en' ? 'Facilities' : 'Fasilitas'}
                        </span>
                    </div>
                )}
                {Boolean(kostFacilitiesRoom) && (
                    <>
                        <div className='w-[4px] h-[4px] bg-[#D9D9D9] rounded-full mx-2 translate-y-2 text-white'>
                            .
                        </div>
                        {kostFacilitiesRoom
                            ?.split(',')
                            .slice(0, 2)
                            .map((kost) => (
                                <>
                                    <span className='text-sm leading-6 whitespace-nowrap'>
                                        {kost}
                                    </span>
                                    <div className='w-[4px] h-[4px] bg-[#D9D9D9] rounded-full mx-2 translate-y-2 text-white'>
                                        .
                                    </div>
                                </>
                            ))}
                    </>
                )}
            </div>
            {isOpen && (
                <>
                    <div className='w-full border-0 border-b border-solid border-gray-200'></div>
                    <div className='py-2 flex flex-wrap'>
                        {/* {Boolean(hotelFacilitiesRoom) && (
                            <>
                                {hotelFacilitiesRoom
                                    ?.split(',')
                                    .slice(2)
                                    .map((hotel, idx, hotelList) => (
                                        <>
                                            <span className='text-sm leading-6 whitespace-nowrap'>
                                                {hotel}
                                            </span>
                                            {hotelList.length > idx + 1 && (
                                                <div className='w-[4px] h-[4px] bg-[#D9D9D9] rounded-full mx-2 translate-y-2' />
                                            )}
                                        </>
                                    ))}
                            </>
                        )} */}
                        {/* {Boolean(villaFacilitiesGeneral) && (
                            <>
                                {villaFacilitiesGeneral
                                    ?.split(',')
                                    .slice(2)
                                    .map((villa, idx, villaList) => (
                                        <>
                                            <span className='text-sm leading-6 whitespace-nowrap'>
                                                {villa}
                                            </span>
                                            {villaList.length > idx + 1 && (
                                                <div className='w-[4px] h-[4px] bg-[#D9D9D9] rounded-full mx-2 translate-y-2' />
                                            )}
                                        </>
                                    ))}
                            </>
                        )} */}
                        {Boolean(kostFacilitiesRoom) && (
                            <>
                                {kostFacilitiesRoom
                                    ?.split(',')
                                    .slice(2)
                                    .map((kost, idx, kostList) => (
                                        <>
                                            <span className='text-sm leading-6 whitespace-nowrap'>
                                                {kost}
                                            </span>
                                            {kostList.length > idx + 1 && (
                                                <div className='w-[4px] h-[4px] bg-[#D9D9D9] rounded-full mx-2 translate-y-2' />
                                            )}
                                        </>
                                    ))}
                            </>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default FacilityInfo;
